import { useEffect, useState } from 'react';

import { EntityRefUtil, RecordState } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetPortal, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { getAssetByIdQueryOptions } from '@/queries/assets';
import { ApiService } from '@/services/api-v2';
import { getWorkspaceRoute } from '@/utils/routes';

import CloseIcon from '../icons/close';
import PlusIcon from '../icons/plus';
import { TablePageHeaderActionButton } from '../table-page/table-page-header';
import { AsyncSelect } from '../ui/async-select';
import { Label } from '../ui/label';

const formSchema = z.object({
  asset: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .optional(),
  location: z.object({
    label: z.string(),
    value: z.string(),
  }),
});

export default function CreateLocationLogDrawer({
  open: openProp,
  onOpenChange,
  assetId,
  noTrigger = false,
  callback,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  assetId?: string;
  noTrigger?: boolean;
  callback?: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(openProp ?? false);

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const handleOpenChange = (open: boolean) => {
    onOpenChange?.(open);
    setOpen(open);
    if (!open) {
      form.reset();
    }
  };

  const { data: assetData } = useQuery({ ...getAssetByIdQueryOptions({ assetId }), enabled: !!assetId && open });

  const { mutate: createAssetLocation } = useMutation({
    mutationFn: async (data: z.infer<typeof formSchema>) => {
      const resolvedAssetId = assetId || data.asset?.value;
      if (!resolvedAssetId) throw new Error('Asset ID is required');

      return await ApiService.CreateAssetLocation({
        assetId: resolvedAssetId,
        locationId: data.location.value,
      });
    },
    onSuccess: () => {
      toast.success('Location log created successfully');
      handleOpenChange(false);
      callback?.();
    },
    onError: (error) => {
      toast.error(`Error creating location log: ${error.message}`);
    },
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  });

  function handleSubmit(data: z.infer<typeof formSchema>) {
    createAssetLocation(data);
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      {!noTrigger && (
        <SheetTrigger asChild>
          <TablePageHeaderActionButton icon={<PlusIcon />} label="Create Location Log" variant="success" kind="primary" onClick={() => setOpen(true)} />
        </SheetTrigger>
      )}
      <SheetPortal>
        <SheetContent hideCloseIcon>
          <form className="flex h-full flex-1 flex-col" onSubmit={form.handleSubmit(handleSubmit)}>
            <SheetHeader className="border-grey-3 flex-row items-center justify-between space-y-0 border-b bg-white">
              <SheetTitle>Create Location Log</SheetTitle>
              <VisuallyHidden>
                <SheetDescription>Create Location Log</SheetDescription>
              </VisuallyHidden>

              <div className="flex items-center gap-2">
                <Button variant="success" size="default" type="submit" disabled={form.formState.isSubmitting || (assetId ? !assetData : !form.watch('asset'))}>
                  Save
                </Button>
                <button
                  type="button"
                  className="data-[state=open]:bg-default-blue rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                  disabled={form.formState.isSubmitting}
                  onClick={() => {
                    handleOpenChange(false);
                  }}>
                  <CloseIcon className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </div>
            </SheetHeader>

            <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
              {assetId ? (
                <div className="flex flex-col gap-1">
                  <Label>Asset</Label>
                  <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 text-sm ring-2 ring-inset">
                    {assetData ? (
                      <Link to={getWorkspaceRoute(`/assets/${assetData.id}`)} className="text-default-blue underline">
                        {EntityRefUtil.createAssetLabel(assetData)}
                      </Link>
                    ) : (
                      <div className="text-sm">-</div>
                    )}
                  </div>
                </div>
              ) : (
                <Controller
                  control={form.control}
                  name="asset"
                  render={({ field: { value, onChange } }) => (
                    <Label className="flex flex-col gap-1">
                      <span>Asset:</span>
                      <AsyncSelect
                        aria-label="Asset"
                        value={value}
                        onChange={onChange}
                        defaultOptions
                        loadOptions={async (input: string) => {
                          const { data } = await (await ApiService.GetAssets({ limit: 100, filters: { search: input } })).json();
                          return data?.map((asset) => ({ value: asset.id, label: EntityRefUtil.createAssetLabel(asset) }));
                        }}
                      />
                    </Label>
                  )}
                />
              )}

              <Controller
                control={form.control}
                name="location"
                render={({ field: { value, onChange } }) => (
                  <Label className="flex flex-col gap-1">
                    <span>Location:</span>
                    <AsyncSelect
                      aria-label="Location"
                      value={value}
                      onChange={onChange}
                      defaultOptions
                      loadOptions={async (input: string) => {
                        const { data } = await (await ApiService.GetLocations({ limit: 100, filters: { state: RecordState.Active, search: input } })).json();
                        return data?.map((location) => ({ value: location.id, label: location.name }));
                      }}
                    />
                  </Label>
                )}
              />
            </div>
          </form>
        </SheetContent>
      </SheetPortal>
    </Sheet>
  );
}
